import { Button } from "@svea/design-system-components-react";
import { Stack, useText } from "@sveabank/cc-common";
import { useMemo, useState } from "react";
import { Field, type FieldInputProps } from "react-final-form";
import { usePageContext } from "../../../../hooks/context";
import type { ClcShareholder } from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import { AddShareholderModal } from "./add-shareholder-modal";
import Shareholder from "./shareholder";

type Props = {
	initialValues: Array<ClcShareholder> | undefined;
};

type ShareholderInput = FieldInputProps<ClcShareholder[], HTMLElement>;

export const AddShareholdersField = ({ initialValues = [] }: Props) => {
	const [t, texts] = useText();

	const [isAddShareholderModalOpen, setIsAddShareholderModalOpen] = useState(false);

	const {
		clcApplicationDetails: { applicant },
	} = usePageContext(useClcCompanyApplication);

	const initialApplicantShareholder: ClcShareholder = {
		email: applicant.email || null,
		nationalId: applicant.nationalId,
		firstName: applicant.firstName,
		lastName: applicant.lastName,
		numberOfShares: 0,
		phone: applicant.phone || { countryCode: "+46", number: "" },
		postalAddress: applicant.postalAddress || null,
		postCode: applicant.postCode,
		town: applicant.town,
	};

	// need to memoize initial value, otherwise final form comparison breaks:
	const initialFieldValue = useMemo(
		() => (initialValues.length > 0 ? initialValues : [initialApplicantShareholder]),
		[applicant],
	);

	const saveShareholder = (input: ShareholderInput) => (shareholder: ClcShareholder) => {
		const newValues = [
			...input.value.filter((s) => s.nationalId !== shareholder.nationalId),
			shareholder,
		];
		input.onChange(newValues);
	};

	const deleteShareholder = (input: ShareholderInput) => (nationalId: string) => {
		const newValues = input.value.filter((v) => v.nationalId !== nationalId);
		input.onChange(newValues);
	};

	return (
		<Stack gap={2} align="start" fullSize>
			<Field<Array<ClcShareholder>> name="shareholders" initialValue={initialFieldValue}>
				{({ input }) => (
					<>
						{input.value.map((shareholder) => {
							const isApplicant = shareholder.nationalId === applicant.nationalId;
							return (
								<Stack key={shareholder.nationalId} gap={2} align="start" fullSize>
									<Shareholder
										shareholder={shareholder}
										isApplicant={isApplicant}
										deleteShareholder={deleteShareholder(input)}
										saveShareholder={saveShareholder(input)}
										allShareholders={input.value}
									/>
								</Stack>
							);
						})}
						<AddShareholderModal
							open={isAddShareholderModalOpen}
							setOpen={setIsAddShareholderModalOpen}
							shareholders={input.value}
							saveShareholder={saveShareholder(input)}
						/>
						<Button
							variant="plain"
							iconStart="plusCircle"
							onPress={() => setIsAddShareholderModalOpen(true)}
						>
							{t(texts.newCompany.shares.addShareholder)}
						</Button>
					</>
				)}
			</Field>
		</Stack>
	);
};
